import dynamic from "next/dynamic"

import Head from "next/head"
import Link from "next/link"
import Router, { useRouter } from "next/router"
import { includes, path, values } from "ramda"
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from "react"

import { useDispatch, useSelector } from "react-redux"
import { actionTypes, makeAction, makePromiseAction } from "states/actions"
import { reducer } from "states/reducers"
import { Container, Dummy, FixedContainer, FloatingTopContainer, FormContainer, Hyperlink, LandingForm, PackageDetailBoxDesktop, PackageDetailBoxTablet, Title } from "styles/index.styles"
import { isNotYetStarted, isPassDueDate } from "utils/dateUtils"
import { PageDefaultProps, PagePropTypes, WithMainLayoutPropTypes } from "utils/propsUtils"
import PageName from "../constants/pageName"
import appendScript from "../utils/appendScript"
import checkCompanyInputValid from "../utils/checkCompanyInputValid"
import Config from "../utils/publicRuntimeConfig"
import { AppConfig, CompanyLandingInfo, OrderInfo, PackageDetails as Package, PaymentInfo, Query } from "../utils/selector"
import str from "../utils/strings"
import useScrollPosition, { getFloatingBottomUpperCut, getFloatingTopLowerCut } from "../utils/useScrollPosition"
import { isNotNullOrEmpty, isset } from "../utils/validate"
import Base from "./layouts/base"
import PackageDetails from "./package-details"

const DynamicErrorWithCustomLoading = dynamic(() => import("../pages/_error"), { loading: () => <p>...</p> })
const DynamicBottomButtonsBar = dynamic(() => import("./bottom-buttons-bar"))
const DynamicPackagePriceAndTitle = dynamic(() => import("./package-price-and-title"), { loading: () => <p>...</p> })
const DynamicPackageDetailModal = dynamic(() => import("./modals/package-details-modal"))

const initState = {
  stickyOnScroll: false,
  stickyTopPos: 0,
  floatingTopOnScroll: false,
  floatingBottomOnScroll: false,
  submitBtnRef: null,
  backBtnRef: null,
  isPaymentGatewayError: false
}

const getDisplayName = (WrappedComponent) => WrappedComponent.displayName || WrappedComponent.name || "Component"

function withMainLayout(WrappedComponent, hocOptions = { withoutTitle: false, TopMessage: null }) {
  const { withoutTitle, TopMessage } = hocOptions
  const currentPageName = getDisplayName(WrappedComponent)

  const Page = ({ query }) => {
    const formRef = useRef(null)
    const stickyRef = useRef(null)
    const baseRef = useRef(null)

    const dispatch = useDispatch()
    const [state, setState] = useReducer(reducer, initState)

    const packageDetails = useSelector(Package)
    const companyInfo = useSelector(CompanyLandingInfo)
    const orderInfo = useSelector(OrderInfo)
    const paymentInfo = useSelector(PaymentInfo)
    const queries = useSelector(Query)
    const { exchangeRate } = useSelector(AppConfig)

    const router = useRouter()

    const isPackageDetailsNotEmpty = isNotNullOrEmpty(packageDetails)
    const isOrderInfoNotEmpty = isNotNullOrEmpty(orderInfo)

    const isDetailPage = useMemo(() => currentPageName.includes(PageName.detail), [currentPageName])

    const trackProps = {
      queries,
      companyInfo,
      orderInfo,
      packageDetails,
      exchangeRate,
      paymentInfo
    }

    useEffect(() => {
      appendScript({
        targetElement: document.body,
        async: true,
        src: Config.footerScriptUrl,
        id: "tdc-footer-script"
      })

      const scrollTop = () => {
        window.scroll({
          top: 0,
          left: 0
        })
      }

      Router.events.on("routeChangeComplete", scrollTop)
      return () => {
        Router.events.off("routeChangeComplete", scrollTop)
      }
    }, [])

    useEffect(() => {
      setTimeout(() => {
        if (includes(currentPageName, values(PageName))) {
          import("../utils/webTrack/trackPage").then((trackPage) => {
            trackPage.default(currentPageName, trackProps)
          })
        }
      }, 0)
    }, [router.asPath])

    useScrollPosition(
      ({ currPos, currStickyPos }) => {
        const topToBottomLength = currStickyPos.height + 85 + 48
        const titleHeight = withoutTitle ? -50 : 35
        const bottomCutOff = currPos.height - topToBottomLength - 102
        const isReachTop = currPos.y <= -titleHeight
        const isReachBottom = currPos.y + bottomCutOff < 0
        const isSticky = isReachTop && !isReachBottom
        const isFloatingTop = currPos.y <= getFloatingTopLowerCut(!withoutTitle)
        const isFloatingBottom = currPos.y + currPos.height >= getFloatingBottomUpperCut(!withoutTitle)
        const topPos = isSticky ? (withoutTitle ? 132 : 48) : isReachTop && isReachBottom ? bottomCutOff - titleHeight : 0
        let nextState = { ...state }
        let isModified = false
        if (isSticky !== state.stickyOnScroll) {
          nextState["stickyOnScroll"] = isSticky
          isModified = true
        }
        if (topPos !== state.stickyTopPos) {
          nextState["stickyTopPos"] = topPos
          isModified = true
        }
        if (isFloatingTop !== state.floatingTopOnScroll) {
          nextState["floatingTopOnScroll"] = isFloatingTop
          isModified = true
        }
        if (isFloatingBottom !== state.floatingBottomOnScroll) {
          nextState["floatingBottomOnScroll"] = isFloatingBottom
          isModified = true
        }
        if (isModified) setState(nextState)
      },
      [state],
      baseRef,
      stickyRef,
      false
    )

    const handlePaymentGatewayError = useCallback(() => {
      setState({ isPaymentGatewayError: true })
    }, [])

    const forwardRef = useCallback((submitBtnRef, backBtnRef) => {
      setState({ submitBtnRef, backBtnRef })
    }, [])

    const handleShowMoreDetail = useCallback(() => {
      dispatch(makeAction(actionTypes.APP_SET_PACKAGE_DETAILS_MODAL_SHOW, { showPackageDetailsModal: true }))
    }, [])
    const handleBack = useCallback(() => state.backBtnRef.current && state.backBtnRef.current.click(), [state.backBtnRef])
    const handleSubmit = useCallback(() => state.submitBtnRef.current && state.submitBtnRef.current.click(), [state.submitBtnRef])

    if (currentPageName.includes(PageName.summary) || currentPageName.includes(PageName.thankyou) || currentPageName.includes(PageName.failure)) {
      if (isOrderInfoNotEmpty && ((isset(orderInfo.error) && orderInfo.error != false) || !isset(orderInfo.id))) {
        if (isset(orderInfo.statusCode) && orderInfo.statusCode == 402) return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Payment_Gateway_Query_Payment_Status_Failed} />
        return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Load_Order} />
      } else if (state.isPaymentGatewayError) {
        return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Payment_Gateway_Error} />
      }
    } else if (currentPageName.includes(PageName.landing)) {
      if (isPackageDetailsNotEmpty && ((isset(packageDetails.error) && packageDetails.error != false) || !isset(packageDetails.id))) {
        if (isset(packageDetails.statusCode) && packageDetails.statusCode == 422) return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Package_Expired} />
        return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Load_Package_With_Error} />
      } else if (isPackageDetailsNotEmpty) {
        const packageEffectiveStart = path(["effectiveStart"], packageDetails)
        const packageEffectiveEnd = path(["effectiveEnd"], packageDetails)
        const isPackageNotYetStart = isNotYetStarted(packageEffectiveStart)
        const isPackageExpired = isPassDueDate(packageEffectiveEnd)

        const packageEligibleGroups = path(["eligibleGroups"], packageDetails)
        const getEligibleGroupsActive = packageEligibleGroups?.length ? packageEligibleGroups.find((item) => item.status == "ACTIVE") : null
        const isPackageEligibleGroupsPass = getEligibleGroupsActive?.salesChannel?.length
          ? getEligibleGroupsActive.salesChannel.some((itemID) =>
              [
                "ALL", //
                "PUBLIC_FORM",
                "SALES_ORDER_LINK",
                "SALES_ORDER_LINK_MARKETING"
              ].includes(itemID)
            )
          : false
        //console.log({ packageEffectiveStart, packageEffectiveEnd, isPackageNotYetStart, isPackageExpired, packageEligibleGroups, getEligibleGroupsActive, isPackageEligibleGroupsPass })
        if (isPackageNotYetStart) {
          return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Package_Not_Yet_Start} />
        } else if (isPackageExpired) {
          return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Package_Expired} />
        } else if (!packageDetails.isEnabled) {
          return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Package_Disabled} />
        } else if (!isPackageEligibleGroupsPass) {
          return <DynamicErrorWithCustomLoading message={str.TXT_ERROR_Package_Disabled} />
        }
      }
    } else if (currentPageName.includes(PageName.detail) || currentPageName.includes(PageName.contactSales)) {
      if (!checkCompanyInputValid(companyInfo)) {
        return <DynamicErrorWithCustomLoading message={<UnavailablePageError />} />
      }
    }
    let pageTitle = ""
    if (currentPageName === PageName.thankyou) {
      pageTitle = str.TXT_SUBSCRIPTION_Page_Title
    }

    return (
      <>
        <PageHead pagename={currentPageName} />
        <Base ref={(ref) => (baseRef.current = ref)}>
          <Container haveTopMessageStyle={isset(TopMessage)}>
            {isset(TopMessage) && <TopMessage />}
            <FormContainer>
              {!withoutTitle && !isset(TopMessage) && <Title aria-label="Page Title">{pageTitle || str.TXT_LANDING_Page_Title}</Title>}
              <PackageDetailBoxTablet show={true}>
                <PackageDetails showAllDetails={false} onMoreClick={handleShowMoreDetail} />
              </PackageDetailBoxTablet>
              <LandingForm ref={(ref) => (formRef.current = ref)}>
                <WrappedComponent query={query || {}} forwardRef={forwardRef} onPaymentGatewayError={handlePaymentGatewayError} />
              </LandingForm>
            </FormContainer>
            <Dummy />
            <PackageDetailBoxDesktop>
              <FixedContainer sticky={state.stickyOnScroll} topPos={state.stickyTopPos} removeTopMargin={withoutTitle} ref={(ref) => (stickyRef.current = ref)}>
                <PackageDetails />
              </FixedContainer>
            </PackageDetailBoxDesktop>
          </Container>
          {isPackageDetailsNotEmpty && (
            <FloatingTopContainer show={state.floatingTopOnScroll}>
              <DynamicPackagePriceAndTitle />
            </FloatingTopContainer>
          )}
          {isDetailPage && <DynamicBottomButtonsBar show={state.floatingBottomOnScroll} onBackClick={handleBack} onSubmitClick={handleSubmit} />}
          <DynamicPackageDetailModal id="package-details" />
        </Base>
      </>
    )
  }

  Page.getInitialProps = async (props) => {
    console.log("Index.getInitialProps props")
    console.log(props)
    const { store, query } = props.ctx
    const orderID = path(["order-id"], query)
    const otaToken = path(["otaToken"], query)

    let req = props.req
    let isServer = req != null
    if (currentPageName.includes(PageName.landing)) {
      await new Promise((resolve, reject) => {
        store.dispatch(makePromiseAction(actionTypes.APP_INIT_LANDING, resolve, reject, { query }))
      })
    } else if (currentPageName.includes(PageName.detail)) {
      await new Promise((resolve, reject) => {
        store.dispatch(makePromiseAction(actionTypes.APP_INIT_DETAIL, resolve, reject))
      })
    } else if (currentPageName.includes(PageName.summary) || currentPageName.includes(PageName.thankyou) || currentPageName.includes(PageName.failure)) {
      await new Promise((resolve, reject) => {
        store.dispatch(makePromiseAction(actionTypes.APP_INIT_SUMMARY_RELATED, resolve, reject, { orderID, otaToken }))
      })
    }
    return {
      isServer,
      query
    }
  }

  Page.propTypes = PagePropTypes

  Page.defaultProps = PageDefaultProps

  return Page
}

const UnavailablePageError = () => {
  return (
    <span>
      {str.TXT_LANDING_Unavailable_Page_Prefix}{" "}
      <Link href="https://sourcingsupport.hktdc.com/hc/en-us" passHref>
        <Hyperlink target="_blank">{str.TXT_LANDING_Unavailable_Page_Here}</Hyperlink>
      </Link>{" "}
      {str.TXT_LANDING_Unavailable_Page_Suffix}
    </span>
  )
}

const PageHead = ({ pagename }) => {
  switch (pagename) {
    case PageName.landing:
      return (
        <Head>
          <title>{str.TXT_ONLINE_FORM_Meta_Head_Title_Index}</title>
          <meta name="Description" content={str.TXT_ONLINE_FORM_Meta_Description_Title_Index} />
          <meta name="WT.si_n" content="SPCS_Online_Subscription_Form_EN" />
          <meta name="WT.si_p" content="SPCS_Online_Subscription_Form_Step01_BasicProfile_EN" />
          <link rel="preconnect" href={Config.preconnectAccountUrl} />
        </Head>
      )
    case PageName.detail:
      return (
        <Head>
          <title>{str.TXT_ONLINE_FORM_Meta_Head_Title_Details}</title>
          <meta name="Description" content={str.TXT_ONLINE_FORM_Meta_Description_Title_Details} />
          <meta name="WT.si_n" content="SPCS_Online_Subscription_Form_EN" />
          <meta name="WT.si_p" content="SPCS_Online_Subscription_Form_Step02_DetailProfile_EN" />
          <script rel="preconnect" src={Config.googleRecaptchaUrl} async defer></script>
        </Head>
      )
    case PageName.summary:
      return (
        <Head>
          <title>{str.TXT_ONLINE_FORM_Meta_Head_Title_Summary}</title>
          <meta name="Description" content={str.TXT_ONLINE_FORM_Meta_Description_Title_Summary} />
          <meta name="WT.si_n" content="SPCS_Online_Subscription_Form_EN" />
          <meta name="WT.si_p" content="SPCS_Online_Subscription_Form_Step03_OrderSummary_EN" />
        </Head>
      )
    case PageName.thankyou:
      return (
        <Head>
          <title>{str.TXT_ONLINE_FORM_Meta_Head_Title_Thankyou}</title>
          <meta name="Description" content={str.TXT_ONLINE_FORM_Meta_Description_Title_Thankyou} />
          <meta name="WT.si_n" content="SPCS_Online_Subscription_Form_EN" />
          <meta name="WT.si_p" content="SPCS_Online_Subscription_Form_Step04_Thankyou_EN" />
          <meta name="WT.si_cs" content="1" />
        </Head>
      )
    case PageName.failure:
      return (
        <Head>
          <title>{str.TXT_ONLINE_FORM_Meta_Head_Title_Failure}</title>
          <meta name="Description" content={str.TXT_ONLINE_FORM_Meta_Description_Title_Failure} />
          <meta name="WT.si_n" content="SPCS_Online_Subscription_Form_EN" />
          <meta name="WT.si_p" content="SPCS_Online_Subscription_Form_Step04_PaymentFailure_EN" />
        </Head>
      )
    case PageName.contactSales:
      return (
        <Head>
          <title>{str.TXT_ONLINE_FORM_Meta_Head_Title_Contact_Sale}</title>
          <meta name="Description" content={str.TXT_ONLINE_FORM_Meta_Description_Title_Contact_Sale} />
          <meta name="WT.si_n" content="SPCS_Online_Subscription_Form_EN" />
          <meta name="WT.si_p" content="SPCS_Online_Subscription_Form_ContactSales_EN" />
        </Head>
      )
    default:
      return null
  }
}

withMainLayout.propTypes = WithMainLayoutPropTypes

export default withMainLayout
